const LAST_ACTIVITY_KEY = 'last_activity_ts';
const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'] as const;
const ACTIVITY_THROTTLE_MS = 1_000;

function safeGetItem(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSetItem(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // Private browsing / quota / SecurityError — fall back to in-memory only.
  }
}

function safeRemoveItem(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // Ignore.
  }
}

export interface IdleWatcherConfig {
  idleLimitMs: number;
  warningMs: number;
}

let config: IdleWatcherConfig | null = null;
let warningTimer: ReturnType<typeof setTimeout> | null = null;
let timeoutTimer: ReturnType<typeof setTimeout> | null = null;
let lastThrottledWriteAt = 0;

function clearTimers(): void {
  if (warningTimer !== null) {
    clearTimeout(warningTimer);
    warningTimer = null;
  }
  if (timeoutTimer !== null) {
    clearTimeout(timeoutTimer);
    timeoutTimer = null;
  }
}

function scheduleFromLastActivity(): void {
  if (config === null) return;
  clearTimers();

  const lastActivityStr = safeGetItem(LAST_ACTIVITY_KEY);
  const lastActivity = lastActivityStr !== null ? Number(lastActivityStr) : Date.now();
  const elapsed = Date.now() - lastActivity;
  const warningDelay = config.idleLimitMs - config.warningMs - elapsed;
  const timeoutDelay = config.idleLimitMs - elapsed;

  if (timeoutDelay <= 0) {
    // Already past idle threshold — defer one tick so listeners attached
    // immediately after start() still fire.
    timeoutTimer = setTimeout(() => {
      window.dispatchEvent(new CustomEvent('session-idle-timeout'));
    }, 0);
    return;
  }

  if (warningDelay > 0) {
    warningTimer = setTimeout(() => {
      window.dispatchEvent(new CustomEvent('session-idle-warning'));
    }, warningDelay);
  } else {
    // Already in the warning window — fire immediately on next tick
    warningTimer = setTimeout(() => {
      window.dispatchEvent(new CustomEvent('session-idle-warning'));
    }, 0);
  }

  timeoutTimer = setTimeout(() => {
    window.dispatchEvent(new CustomEvent('session-idle-timeout'));
  }, timeoutDelay);
}

function handleActivity(): void {
  if (config === null) return;
  const now = Date.now();
  if (now - lastThrottledWriteAt < ACTIVITY_THROTTLE_MS) return;
  lastThrottledWriteAt = now;
  safeSetItem(LAST_ACTIVITY_KEY, String(now));
  scheduleFromLastActivity();
}

function attachListeners(): void {
  for (const eventName of ACTIVITY_EVENTS) {
    window.addEventListener(eventName, handleActivity, { passive: true });
  }
}

function detachListeners(): void {
  for (const eventName of ACTIVITY_EVENTS) {
    window.removeEventListener(eventName, handleActivity);
  }
}

export const idleWatcher = {
  start(nextConfig: IdleWatcherConfig): void {
    if (config !== null) {
      idleWatcher.stop();
    }
    config = nextConfig;
    lastThrottledWriteAt = 0;

    const existing = safeGetItem(LAST_ACTIVITY_KEY);
    if (existing === null) {
      safeSetItem(LAST_ACTIVITY_KEY, String(Date.now()));
    } else {
      const stored = Number(existing);
      const elapsed = Date.now() - stored;
      if (elapsed < 0) {
        safeSetItem(LAST_ACTIVITY_KEY, String(Date.now()));
      }
    }

    attachListeners();
    scheduleFromLastActivity();
  },

  stop(): void {
    clearTimers();
    detachListeners();
    config = null;
    lastThrottledWriteAt = 0;
  },

  recordActivity(): void {
    if (config === null) return;
    const now = Date.now();
    lastThrottledWriteAt = now;
    safeSetItem(LAST_ACTIVITY_KEY, String(now));
    scheduleFromLastActivity();
  },

  getLastActivity(): number | null {
    const stored = safeGetItem(LAST_ACTIVITY_KEY);
    return stored !== null ? Number(stored) : null;
  },

  clearActivity(): void {
    safeRemoveItem(LAST_ACTIVITY_KEY);
  },
};
